/*
  Review cards in the Frenzy collection grid.

  Loaded with `defer` and held until window load, so it never competes with the
  grid's own render. Frenzy builds the grid client-side inside
  .frenzy_collections_page and re-renders it on filter/sort, so a MutationObserver
  re-injects after every render.

  Copy + config live in the section (main-collection-product-grid.liquid) as a
  JSON blob in <script type="application/json" data-review-cards-data>; this file
  only holds behaviour. The blob is generated from LAA_Top_Reviews_PLP_AB_Test.csv
  — each review carries its CSV `category`. The collection's
  custom.plp_category_event metafield (single value or list, passed through the
  blob as `plp_categories`) picks those categories directly when present;
  otherwise categoryFor() below maps the collection to a category by handle +
  title keywords, so the grid shows the reviews curated for it.

  A/B GATE — reviews are OFF by default. See getVariant() below.
*/
(function () {
  'use strict';

  // ---- A/B gate -----------------------------------------------------------
  // Mirrors the PDP sticky buy box (StickyBuyBoxAB in main-product.liquid).
  //   'version' -> review cards are injected
  //   'default' -> nothing is injected, the grid is pure product (current default)
  var AB_KEY = 'la_ab_collection_reviews_v2'; // v2: LA + NYC relaunch, drops arms stored by the first run
  var DEFAULT_VARIANT = 'default'; // reviews OFF until a visitor is bucketed in

  function getVariant() {
    var v = null;
    try { v = window.localStorage.getItem(AB_KEY); } catch (e) {}
    return (v === 'version' || v === 'default') ? v : DEFAULT_VARIANT;
  }

  // TO SEE THE REVIEWS (QA, or to force them on for everyone):
  //   In the browser console:  CollectionReviewsAB.setVariant('version')
  //   To turn them off again:  CollectionReviewsAB.setVariant('default')
  // The choice is stored in localStorage, so it survives navigation and reloads.
  //
  // To force reviews ON for ALL visitors without touching localStorage, change
  // DEFAULT_VARIANT above to 'version'.
  window.CollectionReviewsAB = {
    key: AB_KEY,
    getVariant: getVariant,
    setVariant: function (v) {
      if (v !== 'version' && v !== 'default') { return null; }
      try { window.localStorage.setItem(AB_KEY, v); } catch (e) {}
      // Apply live, no reload: inject if we just turned on, strip if we turned off.
      if (v === 'version') { start(); } else { removeCards(); }
      return v;
    }
  };
  // -------------------------------------------------------------------------

  var container = null;
  var CONFIG = { interval: 6, stars: 5 };
  var REVIEWS = [];
  var COLLECTION_HANDLE = '';
  var COLLECTION_TITLE = '';
  var PLP_CATEGORIES = []; // normalized custom.plp_category_event value(s)
  var observer = null;

  // Common form for comparing metafield values against CSV categories, built
  // around the Intelligems event identifiers (plp_category_t_shirts,
  // plp_category_tops_and_tanks, ...): lowercase, strip the plp_category_
  // prefix, spell "&" as "and", collapse separators to "_". So the CSV
  // category "Tops & Tanks" and the identifier "plp_category_tops_and_tanks"
  // both become "tops_and_tanks".
  function normalizeCategory(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/^plp_category_/, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  // Reads the copy + settings the section wrote out as JSON.
  function readData() {
    var el = document.querySelector('[data-review-cards-data]');
    if (!el) { return false; }
    var data;
    try { data = JSON.parse(el.textContent); } catch (e) { return false; }
    if (!data || !data.reviews || !data.reviews.length) { return false; }
    REVIEWS = data.reviews;
    COLLECTION_HANDLE = data.collection_handle || '';
    COLLECTION_TITLE = data.collection_title || '';
    // The metafield is a single string or a list, depending on its definition.
    var plp = data.plp_categories;
    if (typeof plp === 'string') { plp = [plp]; }
    PLP_CATEGORIES = Array.isArray(plp)
      ? plp.map(normalizeCategory).filter(Boolean)
      : [];
    if (data.interval) { CONFIG.interval = data.interval; }
    if (data.stars) { CONFIG.stars = data.stars; }
    return true;
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  // Fisher–Yates over a copy, so REVIEWS itself is never reordered.
  function shuffled(list) {
    var out = list.slice();
    for (var i = out.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var swap = out[i];
      out[i] = out[j];
      out[j] = swap;
    }
    return out;
  }

  // Maps the collection to one of the CSV review categories by keyword.
  // Returns '' when nothing matches (e.g. "new-arrivals").
  function categoryFor(handle, title) {
    var h = ((handle || '') + ' ' + (title || '')).toLowerCase();
    function has(words) {
      for (var i = 0; i < words.length; i++) {
        if (h.indexOf(words[i]) !== -1) { return true; }
      }
      return false;
    }
    // Order matters: specific terms first ("sweatpant" before "pant",
    // "tank"/"top" before "shirt").
    if (has(['mask'])) { return 'Face Masks'; }
    if (has(['sweatshirt', 'fleece', 'hoodie', 'sweater', 'pullover'])) { return 'Sweatshirts & Fleece'; }
    if (has(['sweatpant'])) { return 'Pants & Sweatpants'; }
    if (has(['bodysuit', 'unitard', 'leotard', 'dancewear', 'biketard'])) { return 'Bodysuits & Dancewear'; }
    if (has(['legging', 'activewear'])) { return 'Leggings & Activewear'; }
    if (has(['sock', 'hosiery', 'tights'])) { return 'Socks & Hosiery'; }
    if (has(['swim', 'bikini'])) { return 'Swim'; }
    if (has(['intimate', 'underwear', 'bra', 'thong', 'panty', 'brief'])) { return 'Intimates'; }
    if (has(['denim', 'jean', 'overall'])) { return 'Denim'; }
    if (has(['dress', 'skirt', 'skort', 'romper'])) { return 'Dresses & Skirts'; }
    if (has(['short'])) { return 'Shorts'; }
    if (has(['pant', 'trouser', 'jogger'])) { return 'Pants & Sweatpants'; }
    if (has(['outerwear', 'jacket', 'coat', 'vest'])) { return 'Outerwear'; }
    if (has(['shoe', 'footwear', 'jelly', 'slide', 'sandal', 'mule'])) { return 'Shoes'; }
    if (has(['accessor', 'bag', 'scarf', 'belt', 'watch', 'hat'])) { return 'Accessories'; }
    if (has(['shirt', 'tee'])) { return 'T-Shirts'; }
    if (has(['tank', 'top', 'cami'])) { return 'Tops & Tanks'; }
    return '';
  }

  // Splits the reviews into priority pools, highest first:
  //   1. explicitly pinned to this collection handle via `collections`
  //   2. category match — from the custom.plp_category_event metafield when
  //      the collection has one (its value(s) name the categories directly,
  //      compared via normalizeCategory), otherwise from the handle + title
  //      keyword heuristic (categoryFor above)
  //   3. generic reviews (no `collections` and no `category`)
  // If every pool is empty (unmatched collection, all reviews categorised),
  // every review is eligible so the grid is never left without cards.
  function poolsFor(handle) {
    var targeted = REVIEWS.filter(function (r) {
      return handle && r.collections && r.collections.indexOf(handle) !== -1;
    });
    var byCategory;
    if (PLP_CATEGORIES.length) {
      byCategory = REVIEWS.filter(function (r) {
        return r.category && PLP_CATEGORIES.indexOf(normalizeCategory(r.category)) !== -1;
      });
    } else {
      var category = categoryFor(handle, COLLECTION_TITLE);
      byCategory = REVIEWS.filter(function (r) {
        return category && r.category === category;
      });
    }
    var generic = REVIEWS.filter(function (r) {
      return (!r.collections || !r.collections.length) && !r.category;
    });
    if (!targeted.length && !byCategory.length && !generic.length) { return [REVIEWS]; }
    return [targeted, byCategory, generic];
  }

  // Deals reviews in random order, never repeating one until every review has
  // been dealt. Pools are exhausted in priority order, so a collection's own
  // reviews all appear before any generic ones are used. On reshuffle the deck
  // is nudged so the refill cannot lead with the review that just went out —
  // otherwise a card could follow itself across the seam between passes.
  function createDeck(pools) {
    var deck = [];
    var last = null;
    return function draw() {
      if (!deck.length) {
        // Popped from the end, so the lowest-priority pool is concatenated
        // first and the highest ends up on top.
        for (var i = pools.length - 1; i >= 0; i--) {
          deck = deck.concat(shuffled(pools[i]));
        }
        if (deck.length > 1 && deck[deck.length - 1] === last) {
          var top = deck.length - 1;
          var swap = deck[top - 1];
          deck[top - 1] = deck[top];
          deck[top] = swap;
        }
      }
      last = deck.pop();
      return last;
    };
  }

  function buildCell(review) {
    // Frenzy grid columns are .frenzy_grid — build one so the card takes a
    // full column slot instead of stacking inside an existing product.
    var cell = document.createElement('div');
    cell.className = 'frenzy_grid frenzy_review_card__cell';
    cell.setAttribute('data-review-card', 'true');

    var card = document.createElement('div');
    card.className = 'frenzy_review_card';

    var starSvg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">' +
        '<path d="M4.88838 0.22298C5.0351 -0.0743183 5.45904 -0.0743186 5.60576 0.22298L6.91736 2.88056C6.97562 2.99861 7.08825 3.08044 7.21853 3.09937L10.1513 3.52554C10.4794 3.57321 10.6104 3.9764 10.373 4.20781L8.25083 6.27645C8.15655 6.36834 8.11353 6.50074 8.13579 6.6305L8.63677 9.55146C8.69282 9.87823 8.34984 10.1274 8.05639 9.97313L5.43321 8.59404C5.31668 8.53278 5.17746 8.53278 5.06093 8.59404L2.43775 9.97313C2.1443 10.1274 1.80132 9.87823 1.85737 9.55146L2.35835 6.6305C2.38061 6.50074 2.33759 6.36834 2.24331 6.27645L0.121112 4.20781C-0.116295 3.9764 0.0147085 3.57321 0.342797 3.52554L3.27561 3.09937C3.40589 3.08044 3.51852 2.99861 3.57678 2.88056L4.88838 0.22298Z" fill="#2D3237"/>' +
      '</svg>';
    var stars = '';
    for (var i = 0; i < CONFIG.stars; i++) { stars += starSvg; }

    // Card order: stars, title, author, review text, then the product it was
    // left on. With a title, the title carries the heading style and the body
    // sits at text size. Without one, the body takes the heading style.
    var text = '';
    if (review.title) {
      text += '<p class="frenzy_review_card__title">' + escapeHtml(review.title) + '</p>';
      text += '<p class="frenzy_review_card__author">-' + escapeHtml(review.name) + '</p>';
      if (review.review) {
        text += '<p class="frenzy_review_card__body">' + escapeHtml(review.review) + '</p>';
      }
    } else {
      text += '<p class="frenzy_review_card__quote">“' + escapeHtml(review.review) + '”</p>';
      text += '<p class="frenzy_review_card__author">-' + escapeHtml(review.name) + '</p>';
    }

    // The product the review was left on, when one is known. Reviews without
    // a `product` simply omit the line; with a `url` the product name links
    // through to its PDP.
    var productLine = '';
    if (review.product) {
      var productName = review.url
        ? '<a href="' + escapeHtml(review.url) + '">' + escapeHtml(review.product) + '</a>'
        : escapeHtml(review.product);
      productLine = '<p class="frenzy_review_card__product">Review for ' + productName + '</p>';
    }

    card.innerHTML =
      '<div class="frenzy_review_card__inner">' +
        '<div class="frenzy_review_card__stars">' + stars + '</div>' +
        text +
        productLine +
      '</div>';

    cell.appendChild(card);
    return cell;
  }

  function injectCard() {
    if (!container || getVariant() !== 'version') { return; }

    // Frenzy grid columns; exclude any card we've already injected.
    var cells = container.querySelectorAll('.frenzy_grid:not([data-review-card])');
    if (!cells.length) { return; }

    // Already present in this render? Do nothing.
    if (container.querySelector('[data-review-card]')) { return; }

    var row = cells[0].parentNode;
    var interval = Math.max(1, CONFIG.interval);
    var draw = createDeck(poolsFor(COLLECTION_HANDLE));

    // Insert a review card every `interval` products, drawing a fresh review
    // each time. The deck is built per render, so filtering or sorting the
    // collection reshuffles the order.
    // Insert before the original cell node so accumulated offsets don't matter.
    for (var pos = interval; pos < cells.length; pos += interval) {
      row.insertBefore(buildCell(draw()), cells[pos]);
    }
  }

  // Pull every injected card back out — used when the A/B flag is flipped off
  // live, so the grid returns to pure product without a reload.
  function removeCards() {
    if (!container) { return; }
    var cards = container.querySelectorAll('[data-review-card]');
    for (var i = 0; i < cards.length; i++) {
      cards[i].parentNode.removeChild(cards[i]);
    }
  }

  // Debounce so a burst of mutations only triggers one injection attempt.
  var scheduled = false;
  function schedule() {
    if (scheduled) { return; }
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      injectCard();
    });
  }

  // Idempotent: safe to call from window load AND from setVariant('version').
  function start() {
    if (getVariant() !== 'version') { return; }
    if (!container) {
      container = document.querySelector('.frenzy_collections_page');
      if (!container) { return; }
    }
    if (!REVIEWS.length && !readData()) { return; }
    if (!observer) {
      observer = new MutationObserver(schedule);
      observer.observe(container, { childList: true, subtree: true });
    }
    schedule();
  }

  // Hold until the page has finished loading, then let the observer take over.
  // Frenzy often renders the grid after load, which the observer catches.
  if (document.readyState === 'complete') { start(); }
  else { window.addEventListener('load', start); }
})();
